import { useState } from 'react'
import { Input, Button } from '../ui'
import { GoogleButton } from './GoogleButton'
import { useTranslation } from '../../contexts/LanguageContext'

interface RegisterFormProps {
  onSubmit: (email: string, password: string, displayName: string) => Promise<void>
  onGoogleSignIn: () => Promise<void>
  onSwitchToLogin: () => void
  error?: string | null
  loading?: boolean
  hideHeader?: boolean
}

export function RegisterForm({
  onSubmit,
  onGoogleSignIn,
  onSwitchToLogin,
  error,
  loading,
  hideHeader,
}: RegisterFormProps) {
  const { t } = useTranslation()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    if (!displayName.trim()) {
      setLocalError(t.auth.nameRequired)
      return
    }

    if (!email.trim()) {
      setLocalError(t.auth.emailRequired)
      return
    }

    if (!password) {
      setLocalError(t.auth.passwordRequired)
      return
    }

    if (password.length < 6) {
      setLocalError(t.auth.passwordMinLength)
      return
    }

    if (password !== confirmPassword) {
      setLocalError(t.auth.passwordMismatch)
      return
    }

    try {
      await onSubmit(email, password, displayName)
    } catch {
      // Error handled by parent
    }
  }

  const displayError = error || localError

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-gray-900">
            {t.auth.registerTitle}
          </h2>
          <p className="mt-2 text-gray-600">
            {t.auth.registerSubtitle}
          </p>
        </div>
      )}

      <GoogleButton onClick={onGoogleSignIn} loading={loading} />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">{t.auth.orRegisterEmail}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {displayError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{displayError}</p>
          </div>
        )}

        <Input
          label={t.auth.fullName}
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t.auth.fullNamePlaceholder}
          autoComplete="name"
        />

        <Input
          label={t.auth.email}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.auth.emailPlaceholder}
          autoComplete="email"
        />

        <Input
          label={t.auth.password}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t.auth.passwordMinLength}
          autoComplete="new-password"
        />

        <Input
          label={t.auth.confirmPassword}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t.auth.confirmPasswordPlaceholder}
          autoComplete="new-password"
        />

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          isLoading={loading}
        >
          {t.auth.register}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600">
        {t.auth.hasAccount}{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="font-medium text-primary hover:text-primary-hover transition-colors"
        >
          {t.auth.login}
        </button>
      </p>
    </div>
  )
}
